import { useEffect, useRef, useState } from "react";

import { AgentAvatar, AgentMessageText } from "./agent-message-text";
import { getAgentResponse, SUGGESTED_QUESTIONS } from "./gids-engine";

interface Message {
  id: string;
  role: "user" | "agent";
  text: string;
  tipSheets?: string[];
}

function TypingIndicator() {
  return (
    <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
      <AgentAvatar />
      <div
        style={{
          background: "var(--ua-surface)",
          border: "1px solid var(--ua-border)",
          borderRadius: "4px 12px 12px 4px",
          padding: "12px 16px",
          display: "flex",
          gap: "4px",
          alignItems: "center",
        }}
      >
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            style={{
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              background: "var(--ua-muted)",
              display: "inline-block",
              animation: `typing-dot 1.2s ease-in-out ${i * 0.2}s infinite`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

function MessageBubble({ message }: { message: Message }) {
  const isUser = message.role === "user";

  if (isUser) {
    return (
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <div
          style={{
            background: "var(--ua-navy)",
            color: "#fff",
            borderRadius: "12px 4px 4px 12px",
            padding: "12px 16px",
            maxWidth: "72%",
            fontSize: "14px",
            lineHeight: 1.5,
          }}
        >
          {message.text}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
      <AgentAvatar style={{ marginTop: "2px" }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            background: "var(--ua-surface)",
            border: "1px solid var(--ua-border)",
            borderRadius: "4px 12px 12px 12px",
            padding: "14px 16px",
            maxWidth: "88%",
          }}
        >
          <AgentMessageText text={message.text} />
        </div>
        {message.tipSheets && message.tipSheets.length > 0 && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "6px",
              marginTop: "8px",
              paddingLeft: "2px",
            }}
          >
            <span style={{ fontSize: "11px", color: "var(--ua-muted)", paddingTop: "2px" }}>
              Bron:
            </span>
            {message.tipSheets.map((ts) => (
              <span
                key={ts}
                style={{
                  fontSize: "11px",
                  fontWeight: 700,
                  background: "#eef2f9",
                  border: "1px solid #c5d0e8",
                  color: "var(--ua-navy)",
                  borderRadius: "999px",
                  padding: "2px 10px",
                }}
              >
                {ts}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function WelcomeState({ onQuestion }: { onQuestion: (q: string) => void }) {
  return (
    <div style={{ padding: "8px 0 24px" }}>
      <div
        style={{
          display: "flex",
          gap: "12px",
          alignItems: "flex-start",
          marginBottom: "24px",
        }}
      >
        <AgentAvatar style={{ marginTop: "2px" }} />
        <div
          style={{
            background: "var(--ua-surface)",
            border: "1px solid var(--ua-border)",
            borderRadius: "4px 12px 12px 12px",
            padding: "16px 18px",
            maxWidth: "520px",
          }}
        >
          <div style={{ fontSize: "14px", lineHeight: 1.65 }}>
            Goedemiddag. Ik ben Ethische Etna, de ethische gids van de EA SHW, een prototype
            gebaseerd op de officiële Tip Sheets (beleidsrichtlijnen 1–16).
            <br />
            <br />
            Ik help je met vragen over je ethische aanvraag: informed consent, anonimiteit,
            onderzoek met minderjarigen, opnames, vergoedingen en meer.
            <br />
            <br />
            <span style={{ color: "var(--ua-muted)", fontSize: "13px" }}>
              Let op: ik geef richting op basis van beleid. Het secretariaat beslist altijd.
            </span>
          </div>
        </div>
      </div>

      <div style={{ paddingLeft: "44px" }}>
        <p
          style={{
            fontSize: "12px",
            fontWeight: 700,
            color: "var(--ua-muted)",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            marginBottom: "10px",
          }}
        >
          Veelgestelde vragen
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {SUGGESTED_QUESTIONS.map((q) => (
            <button
              key={q}
              onClick={() => onQuestion(q)}
              style={{
                background: "var(--ua-surface)",
                border: "1px solid var(--ua-border)",
                borderRadius: "999px",
                padding: "8px 16px",
                fontSize: "13px",
                color: "var(--ua-navy)",
                cursor: "pointer",
                textAlign: "left",
                lineHeight: 1.3,
                transition: "border-color 0.15s, background 0.15s",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.borderColor = "var(--ua-navy)";
                (e.currentTarget as HTMLButtonElement).style.background = "#f0f3f8";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.borderColor = "var(--ua-border)";
                (e.currentTarget as HTMLButtonElement).style.background = "var(--ua-surface)";
              }}
            >
              {q}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export function VraagbaakPage() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typing]);

  function sendQuestion(question: string) {
    if (!question.trim() || typing) {
      return;
    }

    const userMsg: Message = {
      id: `u-${Date.now()}`,
      role: "user",
      text: question,
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setTyping(true);

    const delay = 900 + Math.floor(question.length * 4);
    setTimeout(
      () => {
        const response = getAgentResponse(question);
        const agentMsg: Message = {
          id: `a-${Date.now()}`,
          role: "agent",
          text: response.text,
          tipSheets: response.tipSheets,
        };
        setMessages((prev) => [...prev, agentMsg]);
        setTyping(false);
      },
      Math.min(delay, 2400),
    );
  }

  return (
    <section>
      <style>{`
        @keyframes typing-dot {
          0%, 60%, 100% { opacity: 0.25; transform: translateY(0); }
          30% { opacity: 1; transform: translateY(-3px); }
        }
      `}</style>

      <div className="page-head">
        <div>
          <p className="eyebrow">Onderzoeker · Hulp &amp; gids</p>
          <h1>Ethische Etna</h1>
          <p className="ua-muted" style={{ marginTop: "6px", maxWidth: "560px", fontSize: "15px" }}>
            Stel een vraag over je ethische aanvraag. Etna is gebaseerd op de EA SHW Tip Sheets en
            geeft richting. Het secretariaat beslist altijd.
          </p>
        </div>
      </div>

      <div
        className="card"
        style={{
          display: "flex",
          flexDirection: "column",
          minHeight: "560px",
          overflow: "hidden",
        }}
      >
        {/* Disclaimer bar */}
        <div
          style={{
            background: "#f2f5f9",
            borderBottom: "1px solid var(--ua-border)",
            padding: "10px 20px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <span
            style={{
              fontSize: "11px",
              fontWeight: 700,
              background: "var(--ua-navy)",
              color: "#fff",
              borderRadius: "4px",
              padding: "2px 7px",
            }}
          >
            AI · Prototype
          </span>
          <span style={{ fontSize: "12px", color: "var(--ua-muted)" }}>
            Gebaseerd op EA SHW Tip Sheets 1–16 · Altijd verifiëren bij het secretariaat
          </span>
        </div>

        {/* Messages area */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "20px 20px 8px",
            minHeight: "380px",
            maxHeight: "60vh",
          }}
        >
          {messages.length === 0 ? (
            <WelcomeState onQuestion={sendQuestion} />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              {messages.map((msg) => (
                <MessageBubble key={msg.id} message={msg} />
              ))}
              {typing && <TypingIndicator />}
              <div ref={bottomRef} />
            </div>
          )}
        </div>

        {/* Suggested chips: shown after conversation starts */}
        {messages.length > 0 && !typing && (
          <div
            style={{
              padding: "10px 20px",
              borderTop: "1px solid var(--ua-border)",
              background: "var(--ua-bg)",
              display: "flex",
              flexWrap: "wrap",
              gap: "6px",
              alignItems: "center",
            }}
          >
            <span
              style={{
                fontSize: "11px",
                color: "var(--ua-muted)",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                marginRight: "4px",
              }}
            >
              Snel vragen:
            </span>
            {SUGGESTED_QUESTIONS.slice(0, 3).map((q) => (
              <button
                key={q}
                onClick={() => sendQuestion(q)}
                style={{
                  fontSize: "12px",
                  background: "var(--ua-surface)",
                  border: "1px solid var(--ua-border)",
                  borderRadius: "999px",
                  padding: "4px 12px",
                  color: "var(--ua-navy)",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {q}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div
          style={{
            padding: "16px 20px",
            borderTop: "1px solid var(--ua-border)",
            background: "var(--ua-surface)",
          }}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              sendQuestion(input);
            }}
            style={{ display: "flex", gap: "10px" }}
          >
            <input
              type="text"
              aria-label="Stel een vraag aan Etna"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Bijv. 'Wat zijn de vereisten voor consent bij minderjarigen?'"
              style={{ flex: 1, fontSize: "14px" }}
              disabled={typing}
              autoFocus
            />
            <button
              type="submit"
              className="btn btn--primary"
              disabled={!input.trim() || typing}
              style={{ whiteSpace: "nowrap" }}
            >
              Vraag stellen
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
